import {
  Alert,
  Anchor,
  Badge,
  Button,
  Group,
  Paper,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import { PiEnvelope, PiWarning } from 'react-icons/pi';
import { Link } from 'wouter';
import {
  useInvoiceDetailsByOfferId,
  useResendInvoice,
} from '@/models/invoices';
import type { TOffer } from '@/models/offers';
import type { EmailDeliveryStatus } from '@/types';

/**
 * Resending a submission we never got an acknowledgement for can duplicate the
 * message, so the control stays disabled while the webhook still has a chance to
 * settle it into delivered or bounced.
 */
const RECONCILIATION_MINUTES = 5;

const STATUS_META: Record<
  EmailDeliveryStatus,
  { label: string; color: string }
> = {
  queued: { label: 'Queued', color: 'gray' },
  submission_unknown: { label: 'Delivery unconfirmed', color: 'orange' },
  accepted: { label: 'Sent', color: 'blue' },
  delivered: { label: 'Delivered', color: 'teal' },
  opened: { label: 'Opened', color: 'green' },
  bounced: { label: 'Bounced', color: 'red' },
  complained: { label: 'Marked as spam', color: 'red' },
  unsubscribed: { label: 'Unsubscribed', color: 'orange' },
  failed: { label: 'Failed', color: 'red' },
};

function ResendButton({
  invoiceId,
  disabledReason,
  unconfirmed,
}: {
  invoiceId: number;
  disabledReason: string | null;
  unconfirmed: boolean;
}) {
  const resend = useResendInvoice(invoiceId);
  const handle = async () => {
    try {
      await resend.mutateAsync();
      notifications.show({ color: 'teal', message: 'Invoice re-sent' });
    } catch (e) {
      notifications.show({
        color: 'red',
        title: 'Not sent',
        message: `${e}`,
      });
    }
  };
  return (
    <Tooltip
      label={disabledReason}
      disabled={!disabledReason}
      multiline
      w={260}
    >
      <Button
        size="xs"
        variant="light"
        leftSection={<PiEnvelope size={14} />}
        onClick={handle}
        loading={resend.isPending}
        disabled={Boolean(disabledReason)}
        color={unconfirmed ? 'orange' : undefined}
      >
        Resend
      </Button>
    </Tooltip>
  );
}

export default function InvoiceDelivery({ offer }: { offer: TOffer }) {
  const { data: invoices } = useInvoiceDetailsByOfferId(offer.id);
  if (!invoices?.length) return null;

  return (
    <Paper withBorder p="md">
      <Text fw={600} fz="sm" mb="sm">
        Invoice delivery
      </Text>
      <Stack gap="sm">
        {invoices.map((inv) => {
          const recipient = inv.invoiceRecipient;
          const latest = inv.latestEmail;
          const status = latest?.status ?? null;
          const meta = status ? STATUS_META[status] : null;
          const unconfirmed = status === 'submission_unknown';
          const withinWindow =
            unconfirmed && latest?.statusAt
              ? dayjs().diff(dayjs(latest.statusAt), 'minute') <
                RECONCILIATION_MINUTES
              : false;

          const disabledReason = recipient?.blocked
            ? (recipient.blockReason ??
              'This invoice has no verified recipient')
            : withinWindow
              ? 'Delivery is still unconfirmed. Waiting a few minutes avoids sending a duplicate.'
              : null;

          return (
            <Stack key={inv.id} gap={4}>
              <Group justify="space-between" wrap="nowrap">
                <Group gap={8} wrap="nowrap">
                  <Anchor
                    component={Link}
                    to={`~/invoices/${inv.id}`}
                    fz="sm"
                    fw={500}
                  >
                    {inv.invoiceNumber}
                  </Anchor>
                  {meta && (
                    <Badge size="xs" color={meta.color} variant="light">
                      {meta.label}
                    </Badge>
                  )}
                </Group>
                <ResendButton
                  invoiceId={inv.id}
                  disabledReason={disabledReason}
                  unconfirmed={unconfirmed}
                />
              </Group>

              {recipient?.blocked ? (
                <Alert color="orange" icon={<PiWarning size={14} />} p="xs">
                  <Text fz="xs">{recipient.blockReason}</Text>
                </Alert>
              ) : (
                <Text fz="xs" c="dimmed">
                  Sends to {recipient?.email || latest?.to || 'unknown'}
                </Text>
              )}

              {unconfirmed && (
                <Text fz="xs" c="orange.7">
                  Delivery is unconfirmed. Resending may send a duplicate.
                </Text>
              )}
              {status === 'bounced' || status === 'failed' ? (
                <Text fz="xs" c="red.7">
                  {latest?.error ??
                    'The provider could not deliver this email.'}
                </Text>
              ) : null}
            </Stack>
          );
        })}
      </Stack>
    </Paper>
  );
}
